import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import type { ComponentProps, ReactNode } from 'react';
import type { TSelectBase } from 'types/general.types';

interface RootProps extends ComponentProps<typeof SelectPrimitive.Root> {
  list?: TSelectBase[];
  valuePlaceholder: string;
}
const HubertSelect = ({ list, valuePlaceholder, ...props }: RootProps) => {
  return (
    <SelectPrimitive.Root {...props}>
      <HubertSelect.Trigger
        className={clsx(
          'inline-flex outline-none',
          'items-center justify-center',
          'bg-primary-500',
          'text-primary-50',
          'h-[35px]',
          'px-[15px]',
          'rounded-md',
          'text-[13px] leading-none',
          // 'shadow-[0_2px_10px]',
          // 'shadow-black/10',
          // 'hover:bg-primary-50',
          'focus:shadow-[0_0_0_1px] focus:shadow-black',
          'data-[placeholder]:text-primary-50',
        )}
      >
        <HubertSelect.Value placeholder={valuePlaceholder} />

        <HubertSelect.Icon asChild>
          <ChevronDown className="ml-2 h-4 w-4" />
        </HubertSelect.Icon>
      </HubertSelect.Trigger>

      <HubertSelect.Portal>
        <HubertSelect.Content className="overflow-hidden rounded-md bg-white shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]">
          <HubertSelect.ScrollUpButton className="flex h-[25px] cursor-default items-center justify-center bg-white text-black">
            <ChevronUp />
          </HubertSelect.ScrollUpButton>
          <HubertSelect.Viewport className="p-[5px]">
            {list &&
              list.map((item: TSelectBase, index: number) => {
                return (
                  <HubertSelect.Item key={`hubert-select-content-${index}`} value={item.value}>
                    <HubertSelect.ItemText>{item.label}</HubertSelect.ItemText>
                    <HubertSelect.ItemIndicator>
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </HubertSelect.ItemIndicator>
                  </HubertSelect.Item>
                );
              })}
          </HubertSelect.Viewport>
          <HubertSelect.ScrollDownButton className="flex h-[25px] cursor-default items-center justify-center bg-white text-black">
            <ChevronDown />
          </HubertSelect.ScrollDownButton>
        </HubertSelect.Content>
      </HubertSelect.Portal>
    </SelectPrimitive.Root>
  );
};

interface TriggerProps extends ComponentProps<typeof SelectPrimitive.Trigger> {
  children: ReactNode;
}
const Trigger = ({ children, ...props }: TriggerProps) => {
  return <SelectPrimitive.Trigger {...props}>{children}</SelectPrimitive.Trigger>;
};

interface ValueProps extends ComponentProps<typeof SelectPrimitive.Value> {
  placeholder: string;
  children?: ReactNode;
}
const Value = ({ placeholder, children, ...props }: ValueProps) => {
  return (
    <SelectPrimitive.Value {...props} placeholder={placeholder}>
      {children}
    </SelectPrimitive.Value>
  );
};

interface IconProps extends ComponentProps<typeof SelectPrimitive.Icon> {
  children: ReactNode;
}
const Icon = ({ children, ...props }: IconProps) => {
  return <SelectPrimitive.Icon {...props}>{children}</SelectPrimitive.Icon>;
};

interface PortalProps extends ComponentProps<typeof SelectPrimitive.Portal> {
  children: ReactNode;
}
const Portal = ({ children, ...props }: PortalProps) => {
  return <SelectPrimitive.Portal {...props}>{children}</SelectPrimitive.Portal>;
};

interface ContentProps extends ComponentProps<typeof SelectPrimitive.Content> {
  children: ReactNode;
}
const Content = ({ children, ...props }: ContentProps) => {
  return <SelectPrimitive.Content {...props}>{children}</SelectPrimitive.Content>;
};

interface ScrollUpButtonProps extends ComponentProps<typeof SelectPrimitive.ScrollUpButton> {
  children: ReactNode;
}
const ScrollUpButton = ({ children, ...props }: ScrollUpButtonProps) => {
  return <SelectPrimitive.ScrollUpButton {...props}>{children}</SelectPrimitive.ScrollUpButton>;
};

interface ViewportProps extends ComponentProps<typeof SelectPrimitive.Viewport> {
  children: ReactNode;
}
const Viewport = ({ children, ...props }: ViewportProps) => {
  return <SelectPrimitive.Viewport {...props}>{children}</SelectPrimitive.Viewport>;
};

interface ItemProps extends ComponentProps<typeof SelectPrimitive.Item> {
  className?: string;
  children: ReactNode;
}
const Item = ({ className = '', children, ...props }: ItemProps) => {
  return (
    <SelectPrimitive.Item
      {...props}
      className={clsx(
        'relative',
        'flex items-center',
        'h-[25px]',
        'px-[25px]',
        'rounded-[3px] text-[13px] leading-none select-none',
        // 'data-[disabled]:text-primary data-[disabled]:pointer-events-none',
        'data-[highlighted]:bg-primary-900 data-[highlighted]:text-primary-50 data-[highlighted]:outline-none',
        className,
      )}
    >
      {children}
    </SelectPrimitive.Item>
  );
};

interface ItemTextProps extends ComponentProps<typeof SelectPrimitive.ItemText> {
  children: ReactNode;
}
const ItemText = ({ children, ...props }: ItemTextProps) => {
  return <SelectPrimitive.ItemText {...props}>{children}</SelectPrimitive.ItemText>;
};

interface ItemIndicatorProps extends ComponentProps<typeof SelectPrimitive.ItemIndicator> {
  children: ReactNode;
}
const ItemIndicator = ({ children, ...props }: ItemIndicatorProps) => {
  return <SelectPrimitive.ItemIndicator {...props}>{children}</SelectPrimitive.ItemIndicator>;
};

interface GroupProps extends ComponentProps<typeof SelectPrimitive.Group> {
  children: ReactNode;
}
const Group = ({ children, ...props }: GroupProps) => {
  return <SelectPrimitive.Group {...props}>{children}</SelectPrimitive.Group>;
};

interface LabelProps extends ComponentProps<typeof SelectPrimitive.Label> {
  children: ReactNode;
}
const Label = ({ children, ...props }: LabelProps) => {
  return <SelectPrimitive.Label {...props}>{children}</SelectPrimitive.Label>;
};

interface SeparatorProps extends ComponentProps<typeof SelectPrimitive.Separator> {
  children: ReactNode;
}
const Separator = ({ children, ...props }: SeparatorProps) => {
  return <SelectPrimitive.Separator {...props}>{children}</SelectPrimitive.Separator>;
};

interface ScrollDownButtonProps extends ComponentProps<typeof SelectPrimitive.ScrollDownButton> {
  children: ReactNode;
}
const ScrollDownButton = ({ children, ...props }: ScrollDownButtonProps) => {
  return <SelectPrimitive.ScrollDownButton {...props}>{children}</SelectPrimitive.ScrollDownButton>;
};

interface ArrowProps extends ComponentProps<typeof SelectPrimitive.Arrow> {
  children: ReactNode;
}
const Arrow = ({ children, ...props }: ArrowProps) => {
  return <SelectPrimitive.Arrow {...props}>{children}</SelectPrimitive.Arrow>;
};

HubertSelect.Trigger = Trigger;
HubertSelect.Value = Value;
HubertSelect.Icon = Icon;
HubertSelect.Portal = Portal;
HubertSelect.Content = Content;
HubertSelect.ScrollUpButton = ScrollUpButton;
HubertSelect.Viewport = Viewport;
HubertSelect.Item = Item;
HubertSelect.ItemText = ItemText;
HubertSelect.ItemIndicator = ItemIndicator;
HubertSelect.Group = Group;
HubertSelect.Label = Label;
HubertSelect.Separator = Separator;
HubertSelect.ScrollDownButton = ScrollDownButton;
HubertSelect.Arrow = Arrow;

export default HubertSelect;
